import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Box, Button, CircularProgress } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { tokens } from '@synergy/theme-palo';
import {
    usePaloDockPlacement,
    usePaloHistoryCount,
    usePaloOpenHistory,
    usePaloShortcut,
} from '@synergy/components-palo';

import { Activity } from '../../../common/models/activity';
import { ActivityComment } from '../../../common/models/activity-comment';
import { DocumentStreamKind } from '../../../common/models/document-stream-entry';
import { PaloStreamList } from './palo-stream-list';
import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';
import { StreamReply } from './stream-reply';
import {
    asGroupedActivityItem,
    createActivityTimelineItem,
    getActivityDocumentName,
    markOldestAsCreated,
    useActivityIconStyles,
} from './activity-timeline-mapper';
import { createCommentTimelineItem } from './comment-timeline-mapper';
import { createMessageTimelineItem } from './message-timeline-mapper';
import { createTransitionTimelineItem } from './transition-timeline-mapper';
import { fetchAllTaxRates } from '../../configuration/finance/tax-rates/store/tax-rates-actions';
import { fetchPaymentTerms } from '../../configuration/finance/payment-terms/store/payment-terms-actions';
import { localesSelector } from '../../../common/selectors/locale-selectors';
import { selectedLanguageSelector } from '../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useDocumentStream } from './use-document-stream';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const c = tokens.color;

const FILTERS: { kind?: DocumentStreamKind; labelId: string; dot?: string }[] = [
    { labelId: 'app.stream.filter.all' },
    { kind: DocumentStreamKind.Status, labelId: 'app.stream.filter.status', dot: c.accent },
    { kind: DocumentStreamKind.Internal, labelId: 'app.stream.filter.internal', dot: c.ok },
    { kind: DocumentStreamKind.Message, labelId: 'app.stream.filter.message', dot: c.warn },
    { kind: DocumentStreamKind.System, labelId: 'app.stream.filter.system', dot: c.ink3 },
];

export interface DetailStreamProps {
    documentType: DocumentType;
    documentId?: number;
    /**
     * Reads the stream of the whole procurement rather than of this document
     * (#2160), and offers the stages below as a second filter.
     */
    chain?: boolean;
    /** The document kinds the chain holds; only meaningful with `chain`. */
    stages?: { value?: string; label: ReactNode }[];
    /** Names the document an entry belongs to, for the badge on its row. */
    originOf?: (documentType: string, documentId: number) => ReactNode;
    /** Reports the size of the stream so the tab can show it. */
    onTotalChange?: (total: number) => void;
    /**
     * A credit note is stored as an invoice, so the log names it one. The mask
     * passes what the document actually is (#2212).
     */
    isCredit?: boolean;
    /**
     * Raise this to put the cursor in the remark field.
     *
     * For the masks where the stream shares the dock with other panels: the
     * key that writes a remark has to bring the panel up first, and by then
     * the panel is mounting, so it cannot take the focus itself.
     */
    focusDraft?: number;
}

/**
 * The history of a document as one stream (#2154).
 *
 * Replaces the two tabs "activities" and "transitions": entries of one used to
 * fall between entries of the other without either tab showing it, and the
 * activity tab stopped after 30 entries with no way to reach what came before.
 */
export const DetailStream = ({
    documentType,
    documentId,
    chain,
    isCredit,
    stages,
    originOf,
    onTotalChange,
    focusDraft,
}: DetailStreamProps): ReactElement => {
    const intl = useIntl();
    const classes = useActivityIconStyles();
    const dispatch = useThunkDispatch();
    const user = useAppSelector(state => state.user);
    const locales = useAppSelector(localesSelector);
    const selectedLocale = useAppSelector(selectedLanguageSelector);
    // The change set stores accounts and contacts by id alone; both lists are
    // already in the store for the pickers, so the sentence can name them.
    const accounts = useAppSelector(state => state.accounts.flat);
    const contacts = useAppSelector(state => state.contacts.flat);
    // The same for the two configuration lists the log points at by id: a
    // payment term and a tax account both carry their own wording (R-24).
    const paymentTerms = useAppSelector(state => state.paymentTerms.paymentTerms.items);
    const taxRates = useAppSelector(state => state.taxRates.allTaxRates);
    const stream = useDocumentStream(documentType, documentId, chain);
    // Docked at the right the panel is a column, not a bar.
    const narrow = 'right' === usePaloDockPlacement();
    const { complete, entries, kind, loading, reload, replaceActivity, stage, total } = stream;
    const draftRef = useRef<HTMLInputElement | null>(null);
    const openHistory = usePaloOpenHistory();
    // The draft lives in the history tab, which is hidden while the form is
    // on show; focus once the tab has made it visible.
    const focusTheDraft = useCallback(() => {
        openHistory();
        requestAnimationFrame(() => draftRef.current?.focus());
    }, [openHistory]);

    // The document's own history labels its tab; the procurement's, shown in
    // the documents tab, is not the one the tab stands for.
    usePaloHistoryCount(total, !chain);

    // The study's "C — Vermerk schreiben, springt in den Verlauf". The panel
    // registers it itself, because it is the only thing that knows the field.
    usePaloShortcut(
        documentId
            ? {
                  combo: 'c',
                  group: 'Beleg',
                  label: 'Vermerk schreiben',
                  handler: focusTheDraft,
              }
            : undefined
    );

    useEffect(() => {
        if (focusDraft) {
            focusTheDraft();
        }
    }, [focusDraft, focusTheDraft]);

    useEffect(() => {
        onTotalChange?.(total);
    }, [onTotalChange, total]);

    // Fetched once and kept: both lists are short, and without them an edit
    // reads "Zahlungsbedingung von #3 auf #171".
    useEffect(() => {
        if (0 === paymentTerms.length) {
            dispatch(fetchPaymentTerms(new URLSearchParams({ limit: '200', page: '1' })));
        }

        if (0 === taxRates.length) {
            dispatch(fetchAllTaxRates());
        }
        // Once per mount; the lists are what the effect would otherwise chase.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const patchComments = (activityId: number, next: (comments: ActivityComment[]) => ActivityComment[]) => {
        const entry = entries.find(item => item.activity?.id === activityId);

        if (entry?.activity) {
            replaceActivity(Object.assign(new Activity(), entry.activity, { comments: next(entry.activity.comments) }));
        }
    };

    const failed = () => dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));

    const handleCommentUpdate = async (activityId: number, commentId: number, text: string) => {
        const response = await dispatch((_dispatch, _getState, { api }) =>
            api.activities.updateComment(activityId, commentId, text, documentType)
        );

        if (!response.success || !response.result) {
            failed();
            return;
        }

        const comment = response.result;
        patchComments(activityId, comments => comments.map(item => (item.id === commentId ? comment : item)));
    };

    const handleCommentDelete = async (activityId: number, commentId: number) => {
        const response = await dispatch((_dispatch, _getState, { api }) =>
            api.activities.deleteComment(activityId, commentId, documentType)
        );

        if (!response.success) {
            failed();
            return;
        }

        patchComments(activityId, comments => comments.filter(item => item.id !== commentId));
    };

    const handleCommentWrite = async (text: string) => {
        if (!documentId) {
            return;
        }

        const response = await dispatch((_dispatch, _getState, { api }) =>
            api.activities.addDocumentComment(documentType, documentId, text)
        );

        if (!response.success) {
            failed();
            return;
        }

        // The new remark is the newest entry; reloading the first page is both
        // the simplest way to place it and the honest count.
        stream.reload();
    };

    // Memoised because the mapped entries hold on to them; a new function each
    // render would rebuild every item of the stream on every render.
    const handleDocumentCommentUpdate = useCallback(
        async (commentId: number, text: string) => {
            const response = await dispatch((_dispatch, _getState, { api }) =>
                api.activities.updateDocumentComment(commentId, text)
            );

            if (!response.success) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
                return;
            }

            reload();
        },
        [dispatch, reload]
    );

    const handleDocumentCommentDelete = useCallback(
        async (commentId: number) => {
            const response = await dispatch((_dispatch, _getState, { api }) =>
                api.activities.deleteDocumentComment(commentId)
            );

            if (!response.success) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
                return;
            }

            reload();
        },
        [dispatch, reload]
    );

    const items = useMemo((): PoolTimelineItem[] => {
        const context = {
            intl,
            classes,
            locales,
            selectedLocale,
            currentUserId: user.id,
            accounts,
            contacts,
            paymentTerms,
            taxRates,
            isCredit,
        };
        const activities = entries
            .map(entry => entry.activity)
            .filter((activity): activity is Activity => activity !== null);

        const result: PoolTimelineItem[] = [];
        const activityItems: PoolTimelineItem[] = [];

        entries.forEach(entry => {
            // The badge on the row and the filter chip have to agree, so both
            // read the kind the backend decided rather than deriving it twice.
            const withKind = (item: PoolTimelineItem): PoolTimelineItem => ({
                ...item,
                kind: entry.kind,
                origin:
                    originOf && entry.documentType && entry.documentId
                        ? originOf(entry.documentType, entry.documentId)
                        : undefined,
            });

            // A save that wrote more rows than the stream can show one by one
            // speaks once, for all of them (R-61).
            const withCount = (item: PoolTimelineItem): PoolTimelineItem =>
                entry.members > 1 && entry.activity
                    ? asGroupedActivityItem(withKind(item), entry.activity, entry.members, context)
                    : withKind(item);

            if (entry.message) {
                result.push(withKind(createMessageTimelineItem(entry.message, { intl })));
            } else if (entry.comment) {
                result.push(
                    withKind(
                        createCommentTimelineItem(entry.comment, {
                            intl,
                            onUpdate: handleDocumentCommentUpdate,
                            onDelete: handleDocumentCommentDelete,
                        })
                    )
                );
            } else if (entry.activity) {
                const item = withCount(createActivityTimelineItem(entry.activity, context, activities));

                activityItems.push(item);
                result.push(item);
            } else if (entry.transition) {
                result.push(
                    withKind(
                        createTransitionTimelineItem(entry.transition, {
                            intl,
                            sourceDocumentType: documentType,
                            sourceDocumentId: documentId ?? 0,
                        })
                    )
                );
            }
        });

        // Which entry created the document is a statement about the whole
        // history: under a filter, or with pages still to come, the oldest
        // entry at hand is simply not the oldest one.
        if (complete && !kind && activities.length > 0) {
            markOldestAsCreated(activityItems, getActivityDocumentName(activities[0], context), context);
        }

        return result;
    }, [
        accounts,
        classes,
        complete,
        contacts,
        paymentTerms,
        taxRates,
        documentId,
        documentType,
        entries,
        isCredit,
        handleDocumentCommentDelete,
        handleDocumentCommentUpdate,
        intl,
        kind,
        locales,
        originOf,
        selectedLocale,
        user.id,
    ]);

    return (
        // The dock pads its content; the head sits flush against its edges.
        <Box sx={{ margin: '-9px -13px' }}>
            <Box
                sx={{
                    display: 'flex',
                    // In a column the head folds: the four kind filters need the
                    // whole width to themselves, so the title steps above them
                    // instead of beside them (#2184).
                    flexDirection: narrow ? 'column' : 'row',
                    alignItems: narrow ? 'stretch' : 'center',
                    gap: narrow ? '7px' : '9px',
                    padding: '9px 13px',
                    borderBottom: `1px solid ${c.line}`,
                    backgroundColor: c.surfaceAlt,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
            >
                {/* The tab above already names the panel and counts it, so in a
                    column the title would only say it twice. */}
                {!narrow && (
                    <>
                        <Box component="span" sx={{ fontSize: '12.5px', fontWeight: 620, color: c.ink }}>
                            {/* Both streams can be on screen at once — the procurement's
                                in the tab, the document's in the dock — so they cannot
                                both be called "Verlauf". */}
                            <FormattedMessage id={chain ? 'app.chain.stream.title' : 'app.stream.title'} />
                        </Box>
                        <Box component="span" sx={{ fontSize: '10.5px', color: c.ink3 }}>
                            <FormattedMessage id="app.stream.entryCount" values={{ count: total }} />
                        </Box>
                    </>
                )}

                <Box
                    sx={{
                        marginLeft: narrow ? undefined : 'auto',
                        display: 'flex',
                        gap: '3px',
                        flexWrap: narrow ? 'wrap' : 'nowrap',
                    }}
                >
                    {FILTERS.map(filter => {
                        const active = kind === filter.kind;

                        return (
                            <Box
                                key={filter.labelId}
                                role="button"
                                aria-pressed={active}
                                onClick={() => stream.setKind(filter.kind)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10.5px',
                                    // A touch tighter in the column, so the five
                                    // kinds share one line instead of leaving one
                                    // of them stranded below.
                                    padding: narrow ? '3px 7px' : '3px 9px',
                                    borderRadius: '11px',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? c.ink : c.lineHard}`,
                                    backgroundColor: active ? c.ink : c.surface,
                                    color: active ? c.inkInvert : c.ink2,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {filter.dot && (
                                    <Box
                                        component="span"
                                        sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: filter.dot }}
                                    />
                                )}
                                <FormattedMessage id={filter.labelId} />
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* The chain's own filter: which document kind to hear from. It sits
                below the kinds because it narrows a different thing — the who,
                not the what. */}
            {stages && stages.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: '3px',
                        flexWrap: 'wrap',
                        padding: '7px 13px',
                        borderBottom: `1px solid ${c.line}`,
                    }}
                >
                    {stages.map(entry => {
                        const active = stage === entry.value;

                        return (
                            <Box
                                key={entry.value ?? 'all'}
                                role="button"
                                aria-pressed={active}
                                onClick={() => stream.setStage(entry.value)}
                                sx={{
                                    fontSize: '10.5px',
                                    padding: '3px 9px',
                                    borderRadius: '11px',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? c.accent : c.lineHard}`,
                                    backgroundColor: active ? c.accentSoft : c.surface,
                                    color: active ? c.accent : c.ink2,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {entry.label}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* A remark about the document, not about one of its entries — the
                only thing that has no entry to hang on, so it sits above them. */}
            {documentId && !chain && (
                <Box sx={{ padding: '9px 13px 0' }}>
                    <StreamReply
                        onSubmit={handleCommentWrite}
                        inputRef={draftRef}
                        placeholderId={
                            // Contacts, suppliers and products carry the same
                            // stream, and none of them is a "Beleg".
                            [
                                DocumentType.Contact,
                                DocumentType.Product,
                                DocumentType.Supplier,
                                DocumentType.Customer,
                            ].includes(documentType)
                                ? 'app.stream.comment.placeholder.record'
                                : 'app.stream.comment.placeholder'
                        }
                        submitId="app.stream.comment.submit"
                    />
                </Box>
            )}

            {0 === items.length && !loading && (
                <Box sx={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: c.ink3 }}>
                    <FormattedMessage id="app.stream.empty" />
                </Box>
            )}

            <PaloStreamList items={items} onCommentUpdate={handleCommentUpdate} onCommentDelete={handleCommentDelete} />

            {!complete && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '8px 0 12px' }}>
                    <Button size="small" variant="outlined" onClick={stream.loadMore} disabled={loading}>
                        {loading ? (
                            <CircularProgress size={14} />
                        ) : (
                            <FormattedMessage id="app.stream.loadMore" values={{ remaining: total - entries.length }} />
                        )}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
